import { useReactQueryDevTools } from '@dev-plugins/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { type ComponentProps } from 'react';

import { useAppStateFocus } from './use-app-state-focus';
import { useOnlineManager } from './use-online-manager';

export { useQuery, useQueryClient, useSuspenseQuery, useMutation } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
});

const persistOptions: ComponentProps<typeof PersistQueryClientProvider>['persistOptions'] = {
  persister: asyncStoragePersister,
  dehydrateOptions: {
    shouldDehydrateQuery: query => {
      return query.meta?.persist !== false;
    },
  },
};

// Resolves when the persister has finished rehydrating the query cache from
// disk. Callers that need to read persisted data at bootstrap (e.g. the splash
// gate deciding evergreen vs LTO) can race this promise against a timeout so a
// slow AsyncStorage read never hangs startup.
let resolveRehydration: () => void;
export const queryClientRehydrated = new Promise<void>(resolve => {
  resolveRehydration = resolve;
});

export const ClientProvider = (props: React.PropsWithChildren) => {
  useAppStateFocus();
  useOnlineManager();

  useReactQueryDevTools(queryClient);
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={() => resolveRehydration()}
    >
      {props.children}
    </PersistQueryClientProvider>
  );
};
